import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import makeStyles from "@mui/styles/makeStyles";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import { List, ListItem, ListItemText, Box, Divider, Link } from "@mui/material";
import { AccountCircle, ArrowDropDown } from "@mui/icons-material";
import { Stack, styled, FinanceableLogo } from "@financeable-com-au/financeable-ui";
import AnnounceKit from "announcekit-react";
import { usePostHog } from "posthog-js/react";
import AlertBanner from "../components/Banner/AlertBanner";
import StartApplication from "../components/Application/StartApplication";
import { logout, userSelector } from "../store/slices/userSlice";
import { themeSelector } from "../store/slices/themeSlice";
import { useAnnounceKitBanner } from "../hooks/useAnnounceKitBanner";
import { ROUTES, SERVICE_INVOICE_PAID_BY_TYPES, userType, VITE_ANNOUNCE_KIT_WIDGET } from "../constants";
import { useFeatureFlags } from "../utils/featureFlags/useFeatureFlags";

const Logo = styled("img")`
  max-height: 60px;
`;

const useStyles = makeStyles((theme) => ({
  grow: {
    flexGrow: 1,
  },
  title: {
    display: "block",
  },
  searchIcon: {
    height: "100%",
    position: "absolute",
    pointerEvents: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  inputRoot: {
    color: "inherit",
  },
  inputInput: {
    width: "100%",
  },
  sectionDesktop: {
    display: "none",
  },
  sectionMobile: {
    display: "flex",
  },
}));

export default function TopMenu({ open }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const classes = useStyles();
  const { isCommissionEnabled, isDemoCommercialApplicationEnabled } = useFeatureFlags();
  const isAnnounceKitBanner = useAnnounceKitBanner();
  const user = useSelector(userSelector);
  const { permissions } = user;
  const { theme } = useSelector(themeSelector);
  const isAdminUser = user?.isAdmin;
  const isOwnerOrAdminUser =
    isAdminUser ||
    [userType.admin, userType.owner].includes(user?.userType) ||
    [userType.admin, userType.owner].includes(permissions?.userType);
  const canViewCommissionReport = isCommissionEnabled() && Boolean(permissions?.commission?.report_view);
  const canViewReporting = canViewCommissionReport || isOwnerOrAdminUser;
  const reportingRoute = isOwnerOrAdminUser ? ROUTES.REPORTING.APPLICATIONS_DATA : ROUTES.REPORTING.COMMISSION;

  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState(null);

  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

  const handleLogout = async () => {
    const returnTo = `${window.location.origin}`;
    const response = await dispatch(logout(returnTo));
    let redirectUrl = response?.payload?.data?.redirectUrl;
    if (redirectUrl) {
      const url = new URL(redirectUrl);
      const returnToParam = url.searchParams.get("return_to");
      if (returnToParam) {
        url.searchParams.set("return_to", returnToParam.replace(/\/$/, ""));
        redirectUrl = url.toString();
      }
    }
    sessionStorage.removeItem("csrfToken");
    window.location.replace(redirectUrl || returnTo);
  };
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  // Identify user in PostHog when there is a change in id.
  const posthog = usePostHog();
  useEffect(() => {
    if (user?._id) {
      posthog?.identify(user._id, {
        firstName: user.firstName,
        lastName: user.lastName,
        userType: user.userType,
        email: user.email?.address,
        organisationName: user.organisation?.name,
        workOSOrgId: user.organisation?.workOSId,
      });
    }
  }, [user?._id]);

  const mobileMenuId = "primary-search-account-menu-mobile";
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    ></Menu>
  );

  return (
    <div className={classes.grow}>
      <AppBar
        open={open}
        position="fixed"
        elevation={1}
        style={{
          background: "#000000",
          borderBottom: "1px solid rgba(213, 213, 213, 1)",
          zIndex: 1000,
          color: "#ffffff",
          boxShadow: "none",
        }}
      >
        {isAnnounceKitBanner?.status && <AlertBanner bannerHeight={isAnnounceKitBanner?.height} />}

        <Toolbar>
          {theme?.tenancyLogo ? (
            <Stack mr={2}>
              <Logo src={theme?.tenancyLogo} alt="Logo" />
            </Stack>
          ) : (
            <Stack mr={10}>
              <FinanceableLogo variant="light" width="100px" />
            </Stack>
          )}
          <List
            component="nav"
            aria-label="main mailbox folders"
            style={{ display: "flex", flexDirection: "row", padding: 0 }}
          >
            {!isDemoCommercialApplicationEnabled() && permissions?.application?.quote_view && (
              <ListItem style={{ cursor: "pointer" }} onClick={() => navigate(`/${ROUTES.APP.QUOTES}`)}>
                <ListItemText primary="Quotes" primaryTypographyProps={{ style: { fontWeight: 500 } }} />
              </ListItem>
            )}

            {permissions?.application?.edit && (
              <ListItem style={{ cursor: "pointer" }} onClick={() => navigate(`/${ROUTES.APP.APPLICATIONS}`)}>
                <ListItemText primary="Applications" />
              </ListItem>
            )}

            {!isDemoCommercialApplicationEnabled() && !isAdminUser && (
              <ListItem style={{ cursor: "pointer" }} onClick={() => navigate(`/${ROUTES.APP.CALCULATOR}`)}>
                <ListItemText primary="Calculations" />
              </ListItem>
            )}

            {permissions?.user?.access !== "self" && (
              <ListItem style={{ cursor: "pointer" }} onClick={() => navigate(`/${ROUTES.APP.USERS}`)}>
                <ListItemText primary="Users" />
              </ListItem>
            )}

            {permissions?.org?.view && (
              <ListItem style={{ cursor: "pointer" }} onClick={() => navigate(`/${ROUTES.APP.ORGANISATION}`)}>
                <ListItemText primary="Organisation" />
              </ListItem>
            )}

            {isAdminUser && (
              <>
                <ListItem style={{ cursor: "pointer" }} onClick={() => navigate(ROUTES.ADMIN.TENANTS)}>
                  <ListItemText primary="Tenants" />
                </ListItem>
                <ListItem style={{ cursor: "pointer" }} onClick={() => navigate(ROUTES.ADMIN.INVOICES)}>
                  <ListItemText primary="Invoices" />
                </ListItem>
                <ListItem style={{ cursor: "pointer" }} onClick={() => navigate(ROUTES.ADMIN.SERVICES)}>
                  <ListItemText primary="Services" />
                </ListItem>
              </>
            )}
          </List>
          <ListItemText
            style={{
              padding: "0 16px",
            }}
          >
            <AnnounceKit
              widget={VITE_ANNOUNCE_KIT_WIDGET}
              boosters={true}
              user={{
                id: user?._id,
                email: user?.email?.address,
                firstName: user?.firstName,
                lastName: user?.lastName,
              }}
            >
              What's new
            </AnnounceKit>
          </ListItemText>

          <div className={classes.grow} />
          <StartApplication />
          <Divider orientation="vertical" variant="middle" flexItem color="white" style={{ marginRight: "20px" }} />
          <Link
            id="menu-button"
            aria-controls={isMenuOpen ? "menu" : undefined}
            aria-haspopup="true"
            aria-expanded={isMenuOpen ? "true" : undefined}
            onClick={handleProfileMenuOpen}
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              color: "#ffffff",
              cursor: "pointer",
              textDecoration: "none",
            }}
          >
            <AccountCircle style={{ marginRight: "5px" }} />
            <Box style={{ display: "flex", flexDirection: "column" }}>
              <Box style={{ display: "flex", flexDirection: "row" }}>
                <Typography style={{ marginRight: "5px", lineHeight: 1.4 }}>{user?.firstName}</Typography>
                <Typography style={{ lineHeight: 1.4 }}>{user?.lastName}</Typography>
              </Box>
              <Typography style={{ fontSize: "10px", lineHeight: 1 }}>{user?.organisation?.name}</Typography>
            </Box>
            <ArrowDropDown />
          </Link>
          <Menu
            id="basic-menu"
            aria-labelledby="menu-button"
            anchorEl={anchorEl}
            open={isMenuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            style={{ zIndex: 1000000000 }}
          >
            <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate(`/${ROUTES.APP.SETTINGS}/general`);
              }}
            >
              Settings
            </MenuItem>

            {(permissions?.billing?.all || user?.service_invoice_paid_by === SERVICE_INVOICE_PAID_BY_TYPES.USER) && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  navigate(`/${ROUTES.APP.BILLING}/services`);
                }}
              >
                Billing
              </MenuItem>
            )}

            {canViewReporting && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  navigate(reportingRoute);
                }}
              >
                Reporting
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                posthog?.reset(true);
                handleLogout();
              }}
            >
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {renderMobileMenu}
    </div>
  );
}
